import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchPortalReviews } from "../../store/allocation/portalReviewSlice";
import { fetchEmployeeList } from "../../store/allocation/employeeDashboardSlice";
import { useEmployeeDashboardAccess } from "../../hooks/useEmployeeDashboardAccess";
import { usePortalReviewAccess } from "../../hooks/usePortalReviewAccess";
import ReviewCard from "../../components/portal-reviews/ReviewCard";
import ReviewSubmitModal from "../../components/portal-reviews/ReviewSubmitModal";
import { DASHBOARD } from "../../components/employee-dashboard/dashboardTheme";
import { CustomEmployeeSelect } from "../../components/employee-dashboard/DashboardFilterControls";
import { PortalReviewStatus } from "../../types/PortalReview";

const EmployeeReviews = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { access, canViewAll, employee } = useEmployeeDashboardAccess();
  const { canSubmit, canManage, canViewInbox } = usePortalReviewAccess();
  const { items, loading, error } = useAppSelector((state) => state.portalReviews);
  const { employees } = useAppSelector((state) => state.employeeDashboard);
  const [openForm, setOpenForm] = useState(false);
  const [status, setStatus] = useState<PortalReviewStatus | "">("");

  const empIdFromUrl = searchParams.get("empId")
    ? Number(searchParams.get("empId"))
    : null;

  const targetEmpId = useMemo(() => {
    if (canViewAll) return empIdFromUrl;
    return employee?.emp_id ?? null;
  }, [canViewAll, empIdFromUrl, employee?.emp_id]);

  useEffect(() => {
    if (!access || !canViewAll) return;
    dispatch(fetchEmployeeList({ ...access }));
  }, [access, canViewAll, dispatch]);

  useEffect(() => {
    dispatch(
      fetchPortalReviews({
        status: status || undefined,
        emp_id: canViewInbox ? targetEmpId : undefined,
      }),
    );
  }, [dispatch, status, canViewInbox, targetEmpId]);

  return (
    <>
      <Helmet>
        <title>Portal Feedback</title>
      </Helmet>
      <div className="space-y-4">
        <div
          className="rounded-2xl px-4 py-4 md:px-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
          style={{ backgroundColor: DASHBOARD.header }}
        >
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-white">Portal feedback</h1>
            <p className="text-white/80 text-xs sm:text-sm mt-0.5">
              Share portal updates with Super Admin, or see the status of feedback you already sent.
            </p>
          </div>
          {canSubmit ? (
            <button
              type="button"
              onClick={() => setOpenForm(true)}
              className="px-4 py-2 rounded-xl bg-white text-[#345952] text-sm font-medium"
            >
              Share feedback
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as PortalReviewStatus | "")}
            className="rounded-xl border border-[#DDDDD8] bg-white px-3 py-2 text-sm"
          >
            <option value="">All statuses</option>
            <option value="pending">New</option>
            <option value="useful">Useful</option>
            <option value="not_useful">Not useful</option>
            <option value="implemented">Implemented</option>
          </select>
          {canViewAll ? (
            <CustomEmployeeSelect
              team={employees}
              value={targetEmpId || ""}
              onChange={(empId) => {
                if (empId) setSearchParams({ empId: String(empId) });
                else setSearchParams({});
              }}
              placeholder="All employees"
              className="w-full sm:w-[220px]"
            />
          ) : null}
        </div>

        {loading ? (
          <p className="text-sm text-[#8A8A85]">Loading feedback…</p>
        ) : error ? (
          <p className="text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-[#E8E8E4] bg-white p-8 text-center text-sm text-[#8A8A85]">
            No feedback yet. Use Feedback in the top bar to share an update, screenshot, or video.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((review) => (
              <ReviewCard
                key={review.review_id}
                review={review}
                canManage={canManage}
                showEmployee={canViewAll}
              />
            ))}
          </div>
        )}
      </div>
      <ReviewSubmitModal open={openForm} onClose={() => setOpenForm(false)} />
    </>
  );
};

export default EmployeeReviews;
